import { tracked } from '@glimmer/tracking';
import ENV from 'client-portal/config/environment';
import Service, { service } from '@ember/service';

const BATCH_SIZE = 25;
const HARD_CAP = 100;
const DEFAULT_ENRICHED_PAGES_TARGET = 2;

/**
 * Clinician-search detail enrichment: batches GET /client-portal-api/clinicians
 * for profile fields after the send-all catalog seeds names + availabilityBlocks.
 * Access only via clinicianSearch (service:clinician-search/index), not as a top-level service.
 */
export default class ClinicianSearchClinicianDetailsService extends Service {
  @service store;

  @tracked enrichmentVersion = 0;

  #enriched = new Set();
  #currentGeneration = 0;

  resetState() {
    this.#currentGeneration += 1;
    this.#enriched.clear();
    this.enrichmentVersion = 0;
  }

  isEnriched(clinicianId) {
    return this.#enriched.has(String(clinicianId));
  }

  /**
   * @param {Array} ids — clinician ids to detail-fetch
   */
  enrich(ids) {
    let unique = [...new Set((ids ?? []).map(id => String(id)).filter(Boolean))];
    if (!unique.length) {
      return;
    }

    void this.#flush(unique);
  }

  /**
   * @param {Array} cohort — filtered send-all records
   * @param {{ pageNumber?: number }} [options]
   */
  enrichCohort(cohort, options = {}) {
    if (!Array.isArray(cohort) || cohort.length === 0) {
      return;
    }

    let gen = ++this.#currentGeneration;
    void this.#runEnrichLoop(cohort, gen, options);
  }

  async #runEnrichLoop(cohort, gen, options = {}) {
    let pageSize = ENV.clinicianSearchPageSize ?? 10;
    let cursor = 0;

    let parsedPageNumber = parseInt(String(options.pageNumber ?? 1), 10);
    if (!Number.isFinite(parsedPageNumber) || parsedPageNumber < 1) {
      parsedPageNumber = 1;
    }
    let pagesTarget = Math.max(DEFAULT_ENRICHED_PAGES_TARGET, parsedPageNumber + 1);
    let visibleCardsTarget = pagesTarget * pageSize;

    let dynamicHardCap = Math.max(HARD_CAP, parsedPageNumber * pageSize + BATCH_SIZE);
    let limit = Math.min(cohort.length, dynamicHardCap);

    while (cursor < limit) {
      if (gen !== this.#currentGeneration || this.#isDestroyedOrDestroying()) {
        return;
      }

      let batch = cohort.slice(cursor, cursor + BATCH_SIZE);
      let batchIds = batch
        .map(record => String(record?.id ?? ''))
        .filter(id => id && !this.#enriched.has(id));

      if (batchIds.length) {
        try {
          await this.#flush(batchIds);
        } catch {
          // Leave ids unmarked so a later enrichCohort/enrich call can retry.
        }
      }

      cursor += BATCH_SIZE;

      if (gen !== this.#currentGeneration || this.#isDestroyedOrDestroying()) {
        return;
      }

      this.enrichmentVersion += 1;

      let visibleWindow = cohort.slice(0, visibleCardsTarget);
      if (
        visibleWindow.length > 0 &&
        visibleWindow.every(record => this.#enriched.has(String(record.id)))
      ) {
        break;
      }
    }
  }

  #isDestroyedOrDestroying() {
    return this.isDestroyed || this.isDestroying;
  }

  async #flush(ids) {
    let toFetch = ids.filter(id => !this.#enriched.has(String(id)));
    if (!toFetch.length) {
      return;
    }

    await this.store.query('clinician', {
      id: toFetch.map(id => String(id)),
      filter: { includeAll: true },
    });

    for (let id of toFetch) {
      this.#enriched.add(String(id));
    }
    this.enrichmentVersion += 1;
  }
}
